<script lang="ts">
  import { getSetting, SETTING_USER_PHONE, setSetting } from '$lib/db/settings';
  import { languageChoice, setLanguage, t, type LanguageChoice } from '$lib/i18n/index.svelte';
  import { isValidNumber, normalizeNumber } from '$lib/protocol/registration';
  import {
    currentSubscription,
    disablePush,
    enablePush,
    pushSupported,
    renewCapabilitiesWith,
    type EnableFailure,
  } from '$lib/push/push';

  import { goBack, navigate } from '../../app/router.svelte';

  const APP_VERSION = '0.1.0';

  const choices: LanguageChoice[] = ['system', 'en', 'pl'];

  // Notifications: unsupported → note; permission denied → note; else a switch
  // driven by whether a push subscription exists.
  type PushState = 'unsupported' | 'denied' | 'off' | 'on' | 'busy';
  let push = $state<PushState>('busy');
  $effect(() => {
    void (async () => {
      if (!pushSupported()) {
        push = 'unsupported';
        return;
      }
      if (Notification.permission === 'denied') {
        push = 'denied';
        return;
      }
      push = (await currentSubscription()) !== null ? 'on' : 'off';
    })();
  });

  let pushFailure = $state<EnableFailure | null>(null);
  let pushFailureDetail = $state<string | undefined>(undefined);

  // Must run inside the click handler — the permission prompt is only allowed
  // in a user gesture.
  async function turnPushOn(): Promise<void> {
    push = 'busy';
    pushFailure = null;
    pushFailureDetail = undefined;
    const result = await enablePush();
    if (!result.ok) {
      pushFailure = result.reason;
      pushFailureDetail = result.detail;
      push = result.reason === 'permission_denied' ? 'denied' : 'off';
      return;
    }
    await renewCapabilitiesWith(result.subscription);
    push = 'on';
  }

  function failureText(reason: EnableFailure): string {
    switch (reason) {
      case 'permission_dismissed':
        return t('push.fail.dismissed');
      case 'no_gateway_key':
        return t('push.fail.noGatewayKey');
      case 'no_service_worker':
        return t('push.fail.notReady');
      case 'subscribe_failed':
        return pushFailureDetail === undefined
          ? t('push.fail.subscribe')
          : `${t('push.fail.subscribe')} (${pushFailureDetail})`;
      case 'permission_denied':
        return t('settings.push.denied');
    }
  }

  async function turnPushOff(): Promise<void> {
    push = 'busy';
    await disablePush();
    push = 'off';
  }

  // The number given at first launch — where companies text verification codes.
  let ownNumber = $state('');
  let ownSaved = $state(false);
  let ownInvalid = $state(false);
  $effect(() => {
    void getSetting<string>(SETTING_USER_PHONE).then((stored) => {
      if (stored !== undefined) ownNumber = stored;
    });
  });

  async function saveOwnNumber(): Promise<void> {
    const normalized = normalizeNumber(ownNumber);
    if (!isValidNumber(normalized)) {
      ownInvalid = true;
      return;
    }
    ownInvalid = false;
    await setSetting(SETTING_USER_PHONE, normalized);
    ownNumber = normalized;
    ownSaved = true;
    setTimeout(() => (ownSaved = false), 2000);
  }
</script>

<header class="bar">
  <button class="back" onclick={goBack} aria-label={t('common.back')}>‹</button>
  <h1>{t('settings.title')}</h1>
</header>

<div class="body">
  <section>
    <h3>{t('settings.language')}</h3>
    <div class="choices">
      {#each choices as choice (choice)}
        <button
          class:on={languageChoice() === choice}
          onclick={() => void setLanguage(choice)}
        >
          {t(`settings.language.${choice}`)}
        </button>
      {/each}
    </div>
  </section>

  <section>
    <h3><label for="own-number">{t('settings.ownNumber')}</label></h3>
    <div class="ownrow">
      <input
        id="own-number"
        type="tel"
        inputmode="tel"
        autocomplete="tel"
        placeholder="+48 600 100 200"
        bind:value={ownNumber}
        oninput={() => (ownInvalid = false)}
      />
      <button class="save" onclick={() => void saveOwnNumber()}>
        {ownSaved ? t('settings.ownNumber.saved') : t('common.save')}
      </button>
    </div>
    {#if ownInvalid}
      <p class="invalid">{t('add.error.bad_request')}</p>
    {/if}
  </section>

  <section>
    <h3>{t('settings.push.title')}</h3>
    <p class="note">{t('settings.push.body')}</p>
    {#if push === 'unsupported'}
      <p class="note">{t('settings.push.unsupported')}</p>
    {:else if push === 'denied'}
      <p class="note">{t('settings.push.denied')}</p>
    {:else if push === 'on'}
      <div class="pushrow">
        <span class="pushstate">{t('settings.push.on')}</span>
        <button class="pushtoggle" onclick={() => void turnPushOff()}>
          {t('settings.push.off')}
        </button>
      </div>
    {:else if push === 'off'}
      <button class="pushenable" onclick={() => void turnPushOn()}>
        {t('settings.push.enable')}
      </button>
    {/if}
    {#if pushFailure !== null && push !== 'denied'}
      <p class="invalid">{failureText(pushFailure)}</p>
    {/if}
  </section>

  <section>
    <p class="about">{t('settings.about')}</p>
  </section>

  <section class="actions">
    <button onclick={() => navigate({ name: 'diagnostics' })}>{t('settings.diagnostics')}</button>
  </section>

  <p class="version">Dialano {APP_VERSION}</p>
</div>

<style>
  .bar {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 10px 12px;
  }
  .back {
    border: 0;
    background: transparent;
    font-size: 28px;
    cursor: pointer;
    color: var(--ink);
    padding: 0 8px;
  }
  h1 {
    margin: 0;
    font-size: 20px;
  }
  .body {
    padding: 0 16px 24px;
    display: flex;
    flex-direction: column;
    gap: 20px;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }
  h3 {
    margin: 0 0 8px;
    font-size: 15px;
    color: var(--ink-soft);
  }
  .choices {
    display: flex;
    gap: 8px;
  }
  .choices button {
    border: 1px solid var(--line);
    background: var(--surface);
    color: var(--ink);
    border-radius: 999px;
    padding: 8px 16px;
    cursor: pointer;
  }
  .choices button.on {
    background: var(--brand);
    color: var(--brand-ink);
    border-color: transparent;
  }
  .about {
    margin: 0 0 6px;
  }
  .note {
    margin: 0;
    font-size: 13px;
    color: var(--ink-soft);
  }
  .ownrow {
    display: flex;
    gap: 8px;
  }
  .ownrow input {
    flex: 1;
    border: 1px solid var(--line);
    border-radius: 10px;
    padding: 10px 12px;
    font-size: 16px;
    background: var(--surface);
    color: var(--ink);
  }
  .ownrow .save {
    border: 0;
    border-radius: 10px;
    background: var(--brand);
    color: var(--brand-ink);
    padding: 0 16px;
    font-weight: 600;
    cursor: pointer;
  }
  .invalid {
    margin: 6px 0 0;
    color: var(--danger);
    font-size: 13px;
  }
  .pushrow {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-top: 8px;
  }
  .pushstate {
    font-size: 15px;
  }
  .pushtoggle {
    border: 1px solid var(--line);
    background: var(--surface);
    color: var(--ink);
    border-radius: 999px;
    padding: 8px 16px;
    cursor: pointer;
  }
  .pushenable {
    margin-top: 8px;
    align-self: flex-start;
    border: 0;
    border-radius: 999px;
    background: var(--brand);
    color: var(--brand-ink);
    padding: 10px 18px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
  }
  .actions button {
    border: 1px solid var(--line);
    background: var(--surface);
    color: var(--ink);
    border-radius: 10px;
    padding: 12px;
    font-size: 15px;
    cursor: pointer;
    width: 100%;
    text-align: left;
  }
  .version {
    color: var(--ink-soft);
    font-size: 12px;
    text-align: center;
  }
</style>
